const AUTOPLAY_OVERLAY_SELECTOR = '[data-autoplay-modal="true"]';
const AUTOPLAY_CONTENT_SELECTOR = '[data-autoplay-scroll="true"]';

const GAME_SCROLL_SELECTORS = [
  '.mobile-game-container',
  '.play-board-wrapper',
  '.bet-list-scrollbar',
] as const;

export function getAutoplayOverlayElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>(AUTOPLAY_OVERLAY_SELECTOR);
}

export function getAutoplayContentElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>(AUTOPLAY_CONTENT_SELECTOR);
}

export function isAutoplayModalOpen(): boolean {
  return !!getAutoplayOverlayElement();
}

export function getGameScrollElement(): HTMLElement | null {
  for (const selector of GAME_SCROLL_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) continue;
    // Prefer non-autoplay scrollers when forwarding game-surface wheel.
    if (el.closest(AUTOPLAY_OVERLAY_SELECTOR)) continue;
    if (el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
  }

  for (const selector of GAME_SCROLL_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el || el.closest(AUTOPLAY_OVERLAY_SELECTOR)) continue;
    return el;
  }

  return null;
}

function scrollElementBy(el: HTMLElement, deltaY: number): boolean {
  const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
  if (maxScroll <= 0) {
    return false;
  }

  const next = Math.max(0, Math.min(maxScroll, el.scrollTop + deltaY));
  if (next === el.scrollTop) {
    return false;
  }

  el.scrollTop = next;
  return true;
}

export function getWheelDeltaY(event: WheelEvent): number {
  if (event.deltaMode === 1) {
    return event.deltaY * 16;
  }
  if (event.deltaMode === 2) {
    return event.deltaY * (window.visualViewport?.height ?? window.innerHeight);
  }
  return event.deltaY;
}

/**
 * Scroll autoplay like the history-round modal: prefer the inner content
 * scroller (`data-autoplay-scroll` / bet-list-scrollbar), then the overlay.
 */
export function scrollAutoplayModalBy(deltaY: number): boolean {
  const content = getAutoplayContentElement();
  if (content && scrollElementBy(content, deltaY)) {
    return true;
  }
  const overlay = getAutoplayOverlayElement();
  if (!overlay) {
    return false;
  }
  return scrollElementBy(overlay, deltaY);
}

/** Scroll the main game shell (mobile container / play board / bet list). */
export function scrollGameShellBy(deltaY: number): boolean {
  const gameScroll = getGameScrollElement();
  if (!gameScroll) {
    return false;
  }
  return scrollElementBy(gameScroll, deltaY);
}

export function routeAutoplayWheelScroll(deltaY: number): boolean {
  return scrollAutoplayModalBy(deltaY) || scrollGameShellBy(deltaY);
}

export function handleAutoplayWheelEvent(event: WheelEvent): boolean {
  if (event.ctrlKey) {
    return false;
  }

  if (!isAutoplayModalOpen()) {
    return false;
  }

  const scrolled = scrollAutoplayModalBy(getWheelDeltaY(event));
  if (!scrolled) {
    return false;
  }

  if (event.cancelable) {
    event.preventDefault();
  }
  event.stopPropagation();
  return true;
}

/** Forward canvas / game-surface wheel to overlay or game scrollers. */
export function handleGameSurfaceWheel(deltaY: number): boolean {
  if (isAutoplayModalOpen()) {
    return scrollAutoplayModalBy(deltaY);
  }
  return scrollGameShellBy(deltaY);
}
